import { useCallback, useEffect, useState } from "react";

const SERVER_KEY = "mobai.serverBase";
const FPS_KEY = "mobai.defaultFps";

export const DEFAULT_SERVER_BASE =
  (import.meta.env.VITE_DEFAULT_SERVER as string | undefined) ?? "http://127.0.0.1:8686";

// In-memory fallback for contexts where `localStorage` throws on access —
// e.g. embedded webviews (Codex, Cursor) that run with strict storage
// partitioning, or private-mode iframes. Values stay only for the session
// but the app still works.
const memStore: Record<string, string> = {};

function readStore(key: string): string | null {
  try {
    const v = localStorage.getItem(key);
    if (v !== null) return v;
  } catch {
    /* fall through to memory */
  }
  return memStore[key] ?? null;
}

function writeStore(key: string, value: string): void {
  memStore[key] = value;
  try {
    localStorage.setItem(key, value);
  } catch {
    /* already mirrored to memStore */
  }
}

function deleteStore(key: string): void {
  delete memStore[key];
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

// Strip trailing slash so path joins are clean.
function normalize(base: string): string {
  return base.replace(/\/+$/, "");
}

export function getServerBase(): string {
  const v = readStore(SERVER_KEY);
  return v ? normalize(v) : DEFAULT_SERVER_BASE;
}

export function setServerBase(base: string): void {
  writeStore(SERVER_KEY, normalize(base));
  window.dispatchEvent(new CustomEvent("mobai:settings"));
}

export function clearServerBase(): void {
  deleteStore(SERVER_KEY);
  window.dispatchEvent(new CustomEvent("mobai:settings"));
}

export function getDefaultFps(): number {
  const v = Number(readStore(FPS_KEY));
  return Number.isFinite(v) && v >= 1 && v <= 30 ? v : 10;
}

export function setDefaultFps(fps: number): void {
  writeStore(FPS_KEY, String(fps));
  window.dispatchEvent(new CustomEvent("mobai:settings"));
}

/**
 * On first load, if the URL includes `?server=<url>`, persist it as the
 * default server base. Then strip the query param so it doesn't stick to
 * subsequent navigations. Called once from main.tsx before render.
 */
export function seedServerBaseFromURL(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const fromURL = params.get("server");
  if (!fromURL) return;
  setServerBase(fromURL);
  params.delete("server");
  const q = params.toString();
  const url = window.location.pathname + (q ? `?${q}` : "") + window.location.hash;
  window.history.replaceState(null, "", url);
}

/**
 * Reactive hook: returns the current serverBase and re-renders when it
 * changes (cross-tab via `storage` event, same-tab via custom event).
 */
export function useServerBase(): [string, (v: string) => void] {
  const [val, setVal] = useState<string>(getServerBase);
  useEffect(() => {
    const onChange = () => setVal(getServerBase());
    window.addEventListener("storage", onChange);
    window.addEventListener("mobai:settings", onChange);
    return () => {
      window.removeEventListener("storage", onChange);
      window.removeEventListener("mobai:settings", onChange);
    };
  }, []);
  const set = useCallback((v: string) => setServerBase(v), []);
  return [val, set];
}
